// Resolución de links e imágenes de la wiki. Funciones puras: el markdown
// trae rutas relativas al doc (como en GitHub) y acá se traducen a rutas de
// la SPA, al endpoint de assets o a GitHub.

export const WIKI_BASE = '/wiki'
export const REPO_BLOB = 'https://github.com/institutohumai/vrs-vr-app/blob/main/'
export const ORG_URL = 'https://github.com/institutohumai/'

export type ResolvedLink =
  | { kind: 'anchor'; href: string }
  | { kind: 'external'; href: string }
  | { kind: 'app'; to: string }
  | { kind: 'doc'; to: string }
  | { kind: 'asset'; path: string; hash: string }
  | { kind: 'repo'; href: string }

const SCHEME = /^[a-z][a-z0-9+.-]*:/i

/** Normaliza una ruta POSIX (sin raíz): resuelve `.` y `..`; los `..` que
 * escapan quedan al principio. */
export function normalizePath(path: string): string {
  const out: string[] = []
  for (const part of path.split('/')) {
    if (part === '' || part === '.') continue
    if (part === '..' && out.length > 0 && out[out.length - 1] !== '..') out.pop()
    else out.push(part)
  }
  return out.join('/')
}

export function dirname(path: string): string {
  const i = path.lastIndexOf('/')
  return i < 0 ? '' : path.slice(0, i)
}

function safeDecode(value: string): string {
  try {
    return decodeURIComponent(value)
  } catch {
    return value
  }
}

/** Ruta de la SPA de un doc (`setup/01-x.md` → `/wiki/setup/01-x`; los
 * `.html` conservan la extensión: `/wiki/ciencia/63-x.html`). */
export function docRoute(path: string, anchor?: string | null): string {
  const route = `${WIKI_BASE}/${path.replace(/\.md$/i, '')}`
  return anchor ? `${route}#${anchor}` : route
}

/** `docPath` es la ruta del doc actual relativa a `wiki/`. */
export function resolveHref(docPath: string, href: string): ResolvedLink {
  const raw = href.trim()
  if (raw.startsWith('#')) return { kind: 'anchor', href: raw }
  if (SCHEME.test(raw) || raw.startsWith('//')) return { kind: 'external', href: raw }
  if (raw.startsWith('/')) return { kind: 'app', to: raw }

  const hashAt = raw.indexOf('#')
  const pathPart = hashAt < 0 ? raw : raw.slice(0, hashAt)
  const hash = hashAt < 0 ? '' : raw.slice(hashAt)
  const cleanPath = safeDecode(pathPart.split('?')[0] ?? '')
  const joined = normalizePath([dirname(docPath), cleanPath].filter(Boolean).join('/'))

  if (joined.startsWith('..')) {
    // Sale de wiki/: código del monorepo (wiki/ está en la raíz) u otro repo.
    const inRepo = normalizePath(`wiki/${joined}`)
    if (!inRepo.startsWith('..')) return { kind: 'repo', href: `${REPO_BLOB}${inRepo}${hash}` }
    const outside = inRepo.replace(/^(\.\.\/)+/, '')
    const [repo, ...rest] = outside.split('/')
    const tail = rest.length ? `/blob/main/${rest.join('/')}` : ''
    return { kind: 'repo', href: `${ORG_URL}${repo}${tail}${hash}` }
  }
  if (/\.(md|html)$/i.test(joined)) {
    return { kind: 'doc', to: `${docRoute(joined)}${hash}` }
  }
  return { kind: 'asset', path: joined, hash }
}

/** `src` de una imagen: dentro de wiki/ va al endpoint de assets. */
export function resolveImageSrc(
  docPath: string,
  src: string,
  assetUrl: (path: string) => string,
): string {
  const resolved = resolveHref(docPath, src)
  if (resolved.kind === 'asset') return assetUrl(resolved.path)
  if (resolved.kind === 'external') return resolved.href
  return src
}

/** Parte la ruta de la SPA (lo que sigue a /wiki/) en doc o capítulo. */
export function routeTarget(
  splat: string,
  docPaths: ReadonlySet<string>,
  chapterIds: ReadonlySet<string>,
): { kind: 'home' } | { kind: 'doc'; path: string } | { kind: 'chapter'; id: string } | { kind: 'missing'; path: string } {
  const clean = normalizePath(safeDecode(splat))
  if (!clean) return { kind: 'home' }
  const asDoc = `${clean}.md`
  if (docPaths.has(asDoc)) return { kind: 'doc', path: asDoc }
  if (docPaths.has(clean)) return { kind: 'doc', path: clean }
  if (chapterIds.has(clean)) return { kind: 'chapter', id: clean }
  return { kind: 'missing', path: asDoc }
}
